import type {
    KeyboardEvent as ReactKeyboardEvent,
    LiHTMLAttributes,
    MouseEvent as ReactMouseEvent,
    ReactElement
} from "react";
import { cloneElement, createRef, useCallback, useEffect, useRef } from "react";

import { css } from "@compiled/react";

import { media } from "@atlaskit/primitives";
import { token } from "@atlaskit/tokens";

import { Image } from "@/libraries/design/components";
import { BgColors } from "@/libraries/design/components/link/src/link";
import { wacToken } from "@/libraries/design/foundations";
import { DataEventProps } from "@/libraries/wpl/foundations/src";

import { useTabsContext, TabFC } from "./";

export type TabProps = {
    dataEventProps?: DataEventProps;
    focus?: boolean;
    tabIndex?: number;
    title: string;
    iconUrl?: string;
    tabSize?: "small" | "large";
    bgColor?: BgColors;
    label?: string;
    enableRovoBorder?: boolean;
    enableResponsiveTabSize?: boolean;
} & LiHTMLAttributes<HTMLLIElement>;

const DraggableTab: TabFC<TabProps> = ({
    id,
    focus,
    tabIndex,
    title,
    iconUrl,
    tabSize = "large",
    bgColor = "bgWhite",
    label,
    dataEventProps,
    className,
    enableRovoBorder = false,
    enableResponsiveTabSize = false,
    children,
    ...props
}: // eslint-disable-next-line complexity
TabProps) => {
    const { goToTab, isCurrentTab, userSelected } = useTabsContext();
    const startX = useRef(0);
    const tabRef = createRef<HTMLLIElement>();

    const isTabSelected = isCurrentTab(tabIndex);

    useEffect(() => {
        if (
            !tabRef.current ||
            !tabRef.current.parentElement ||
            !tabRef.current.parentElement.scrollTo ||
            !tabRef.current.parentElement.parentElement ||
            !tabRef.current.parentElement.parentElement.scrollTo
        ) {
            return;
        }

        // Scroll to the selected tab if it's not in view
        if (isTabSelected) {
            if (tabRef.current.parentElement?.parentElement) {
                // calculate the offset to center the tab in the parent
                const offsetLeft =
                    tabRef.current.offsetLeft -
                    tabRef.current.parentElement.offsetLeft -
                    tabRef.current.parentElement.offsetWidth / 2 +
                    tabRef.current.offsetWidth / 2;

                tabRef.current.parentElement.parentElement.scrollTo({
                    left: offsetLeft,
                    top: 0,
                    behavior: "smooth"
                });
            }
            if (focus) {
                tabRef.current.focus({
                    preventScroll: true
                });
            }
        }

        if (isTabSelected && dataEventProps && userSelected) {
            tabRef.current.dispatchEvent(new Event("select"));
        }
    }, [tabRef, isTabSelected, focus, userSelected, dataEventProps]);

    const onMouseDown = (e: ReactMouseEvent<HTMLLIElement>) => {
        startX.current = e.clientX ?? 0;
        props.onMouseDown?.(e);
    };

    const onClick = useCallback(
        (e: ReactMouseEvent<HTMLLIElement>) => {
            if (Math.abs(e.clientX - startX.current) > 15) {
                // Prevent click event if user is dragging
                return;
            }
            props.onClick?.(e);

            if (isTabSelected) {
                return;
            }

            goToTab(tabIndex);
        },
        [isTabSelected, tabIndex, goToTab, props]
    );

    const onKeyDown = (e: ReactKeyboardEvent) => {
        if (e.code === "Space" || e.code === "Enter") {
            goToTab(tabIndex);
            e.preventDefault();
        }
    };

    const tabColor =
        bgColor === "bgWhite"
            ? wacToken("color.neutral.light200")
            : wacToken("color.neutral.light0");

    const tabStyles = css({
        "whiteSpace": "nowrap",
        "minWidth": 70,
        "backgroundColor": tabColor,
        "alignItems": "center",
        "justifyContent": "center",
        "boxSizing": "border-box",
        "display": "flex",
        "textAlign": "center",
        "textDecoration": "none",
        "borderRadius": wacToken("corner.radius.pill"),
        "border": `2px solid ${tabColor}`,
        "cursor": "pointer",
        ":hover": {
            borderColor: wacToken("color.primary.blue")
        },
        "&.currentTab": {
            ":focus": {
                outlineColor: wacToken("color.primary.black")
            }
        }
    });

    const rovoBorderStyles = css({
        "borderColor": "transparent",
        "&.currentTab": {
            borderColor: "transparent !important",
            backgroundImage: `linear-gradient(${tabColor}, ${tabColor}), conic-gradient(from 38.61deg at 50% 50%, #FCA700 0deg, #FCA700 72.69deg, #82B536 72.73deg, #82B536 160.97deg, #1868DB 161deg, #1868DB 260deg, #BF63F3 260.03deg, #BF63F3 320.4deg, #FCA700 320.75deg, #FCA700 360deg)`,
            backgroundOrigin: "border-box",
            backgroundClip: "padding-box, border-box"
        }
    });

    const borderStyles = css({
        "&.currentTab": {
            backgroundColor: wacToken("color.extended.blue100"),
            borderColor: wacToken("color.primary.blue")
        }
    });

    const largeTabStyles = css({
        padding: `14px 28px`,
        lineHeight: "28px",
        height: "60px",
        fontSize: "1.125rem"
    });

    const smallTabStyles = css({
        padding: `${token("space.075")} ${token("space.300")}`,
        lineHeight: token("space.250"),
        height: "40px",
        fontSize: "1rem"
    });

    const iconTabStyles = css({
        flexDirection: "column",
        borderRadius: "6px",
        fontSize: "1rem"
    });

    const iconLargeTabStyles = css({
        height: "88px",
        minWidth: "140px",
        padding: `${token("space.075")} ${token("space.200")} 10px ${token(
            "space.200"
        )}`
    });

    const iconSmallTabStyles = css({
        height: "74px",
        minWidth: "125px",
        padding: `${token("space.075")} ${token("space.200")}`
    });

    const responsiveIconTabStyles = css({
        height: "74px",
        minWidth: "125px",
        padding: `${token("space.075")} ${token("space.200")}`,
        [media.above.sm]: {
            height: "88px",
            minWidth: "140px",
            padding: `${token("space.075")} ${token("space.200")} 10px ${token(
                "space.200"
            )}`
        }
    });

    const iconLargeContainerStyles = css({
        height: "48px",
        width: "48px"
    });

    const iconSmallContainerStyles = css({
        height: "38px",
        width: "38px"
    });

    const responsiveIconContainerStyles = css({
        height: "38px",
        width: "38px",
        [media.above.sm]: {
            height: "48px",
            width: "48px"
        }
    });

    return (
        <div
            {...props}
            // @ts-ignore
            ref={tabRef}
            // @ts-ignore
            className={className}
            // this is just a container for the tab
        >
            {!!children ? (
                cloneElement(children as ReactElement, {
                    // @ts-ignore
                    onMouseDown,
                    // @ts-ignore
                    onClick,
                    // @ts-ignore
                    onKeyDown,
                    ...dataEventProps
                })
            ) : (
                <button
                    type="button"
                    id={`tab${id}`}
                    role="tab"
                    aria-selected={isTabSelected}
                    aria-controls={`panel${id}`}
                    aria-label={label}
                    title={label}
                    tabIndex={isTabSelected ? 0 : -1}
                    css={[
                        tabStyles,
                        iconUrl && iconTabStyles,
                        iconUrl && enableRovoBorder
                            ? rovoBorderStyles
                            : borderStyles,
                        iconUrl &&
                            enableResponsiveTabSize &&
                            responsiveIconTabStyles,
                        !iconUrl && tabSize === "large" && largeTabStyles,
                        !iconUrl && tabSize === "small" && smallTabStyles,
                        iconUrl &&
                            !enableResponsiveTabSize &&
                            tabSize === "large" &&
                            iconLargeTabStyles,
                        iconUrl &&
                            !enableResponsiveTabSize &&
                            tabSize === "small" &&
                            iconSmallTabStyles
                    ]}
                    className={`${isTabSelected ? "currentTab" : ""}`}
                    // how to adjust for different page? or just remove?
                    data-product-key="template"
                    data-event="clicked"
                    data-event-component="button"
                    data-event-container="tabsMenu"
                    data-label={title}
                    // @ts-ignore
                    onMouseDown={onMouseDown}
                    // @ts-ignore
                    onClick={onClick}
                    // @ts-ignore
                    onKeyDown={onKeyDown}
                    {...dataEventProps}
                >
                    {iconUrl && (
                        <div
                            css={[
                                !enableResponsiveTabSize &&
                                    tabSize === "large" &&
                                    iconLargeContainerStyles,
                                !enableResponsiveTabSize &&
                                    tabSize === "small" &&
                                    iconSmallContainerStyles,
                                enableResponsiveTabSize &&
                                    responsiveIconContainerStyles
                            ]}
                            className="default-icon"
                        >
                            <Image
                                src={`${iconUrl || ""}`}
                                alt={""}
                                draggable={false}
                            />
                        </div>
                    )}
                    {title}
                </button>
            )}
        </div>
    );
};
DraggableTab.tabsRole = "Tab";

export { DraggableTab };
